import { useQuery } from '@tanstack/react-query';
import { Badge, Dialog, EmptyState, Skeleton, Table, type Column } from '@eombor/ui';
import { Boxes, CalendarDays, FileText, User } from 'lucide-react';
import type { SubdivisionStockRow, Unit } from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { subdivisionApi } from '../../subdivision/api/subdivision.api.js';

/** `2026-09-03T…` → `03.09.2026`. */
const formatDate = (iso: string): string => {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export interface SubdivisionStockDialogProps {
  /** `null` — oyna yopiq. */
  subdivision: { id: string; name: string; orgUnitName: string; code: string } | null;
  onClose: () => void;
}

/**
 * Bo'linmaning KICHIK OMBORI — qoldiq va har satrning kelib chiqishi.
 *
 * Ma'lumot `GET /subdivisions/:id/stock` dan keladi, ya'ni hisobot uchun
 * alohida endpoint yozilmagan: bu ayni o'sha ro'yxat va u allaqachon
 * "qachon, qaysi talabnoma bilan, kim qabul qilgan" ni beradi.
 *
 * Doira ham o'sha yerda: `subdivision.stock.read` bo'lmasa 403 keladi va
 * oyna xatoni ochiq ko'rsatadi — jimgina bo'sh jadval ko'rsatilmaydi.
 */
export const SubdivisionStockDialog = ({ subdivision, onClose }: SubdivisionStockDialogProps) => {
  const query = useQuery<{ items: SubdivisionStockRow[]; total: number }, ApiClientError>({
    queryKey: ['subdivision', 'stock', subdivision?.id ?? null],
    queryFn: () => subdivisionApi.stock(subdivision?.id ?? '', { limit: 100 }),
    enabled: subdivision !== null,
  });

  const columns: Column<SubdivisionStockRow>[] = [
    {
      key: 'name',
      header: t('report.stockItem'),
      render: (row) => <span className="font-medium text-text">{row.nomenclatureName}</span>,
    },
    {
      key: 'qty',
      header: t('report.stockQty'),
      align: 'right',
      mono: true,
      render: (row) => (
        <span className="tabular font-semibold text-text">
          {row.qty.toLocaleString('ru-RU')}{' '}
          <span className="font-normal text-text-faint">{unitText(row.unit as Unit)}</span>
        </span>
      ),
    },
    { key: 'batch', header: t('report.stockBatch'), mono: true, render: (row) => row.batchNo },
    {
      key: 'received',
      header: t('report.stockReceived'),
      render: (row) => (
        <span className="tabular inline-flex items-center gap-1.5 text-text-muted">
          <CalendarDays size={13} aria-hidden className="text-text-faint" />
          {formatDate(row.receivedAt)}
        </span>
      ),
    },
    {
      key: 'doc',
      header: t('report.stockDoc'),
      mono: true,
      /* Talabnoma raqami YO'Q bo'lishi mumkin — qoldiq boshqa yo'l bilan
         paydo bo'lgan bo'lsa. Bo'sh katak "—" bilan ko'rsatiladi. */
      render: (row) =>
        row.requisitionDocNo === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          <span className="tabular inline-flex items-center gap-1.5 text-text-muted">
            <FileText size={13} aria-hidden className="text-text-faint" />
            {row.requisitionDocNo}
          </span>
        ),
    },
    {
      key: 'by',
      header: t('report.stockReceivedBy'),
      render: (row) =>
        row.receivedByName === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-text-muted">
            <User size={13} aria-hidden className="text-text-faint" />
            {row.receivedByName}
          </span>
        ),
    },
  ];

  return (
    <Dialog
      open={subdivision !== null}
      onClose={onClose}
      title={subdivision?.name ?? ''}
      description={
        subdivision === null ? undefined : (
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone="accent" mono>
              {subdivision.code}
            </Badge>
            <span className="text-text-muted">{subdivision.orgUnitName}</span>
          </span>
        )
      }
      size="lg"
      error={query.error?.message}
    >
      {query.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('report.loading')}>
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : query.isError ? null : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Badge tone={query.data.total > 0 ? 'accent' : 'neutral'} mono>
              {t('report.stockCount', { count: query.data.total })}
            </Badge>
          </div>
          <Table
            columns={columns}
            rows={query.data.items}
            rowKey={(row) => row.batchId}
            density="compact"
            empty={
              <EmptyState
                icon={<Boxes size={20} />}
                title={t('report.stockEmptyTitle')}
                description={t('report.stockEmptyHint')}
              />
            }
          />
        </div>
      )}
    </Dialog>
  );
};
